import React, { useState } from 'react';
import TransactionTemplatePicker from './TransactionTemplatePicker';
import TemplatePreviewCard from './TemplatePreviewCard';
import { transactionTemplatesService } from '../../services/transactionTemplates.service';
import '../common/ConfirmDialog.css';
import './ApplyTemplateModal.css';

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/**
 * "Apply Template" on an existing transaction. Same dropdown + preview as the create
 * pages. Additive only — the backend skips anything already on the transaction — and
 * the template's forms are added automatically afterwards.
 */
const ApplyTemplateModal = ({ transaction, onClose, onApplied }) => {
  const side = (transaction.side || '').toUpperCase() === 'LISTING' ? 'LISTING' : 'BUYING';
  const agentState = (transaction.agentLicenseState || transaction.propertyState || '').toUpperCase();
  const [templateId, setTemplateId] = useState('');
  const [saleType, setSaleType] = useState(transaction.saleType || 'STANDARD');
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handleApply = async () => {
    setError('');
    setIsApplying(true);
    try {
      // Sale type is only stored when the transaction has none yet.
      setResult(await transactionTemplatesService.applyTemplate(transaction.id, {
        templateId,
        saleType: transaction.saleType ? undefined : saleType,
      }));
    } catch (e) {
      console.error('[ApplyTemplateModal] Apply failed', e);
      setError(e.response?.data?.message || e.response?.data?.error || "Couldn't apply the template. Please try again.");
    } finally {
      setIsApplying(false);
    }
  };

  const handleDone = () => { onApplied(); onClose(); };

  return (
    <div className="confirm-dialog-overlay" onClick={isApplying ? undefined : (result ? handleDone : onClose)}>
      <div className="confirm-dialog tpl-apply-dialog" role="dialog" aria-modal="true" aria-labelledby="tpl-apply-title"
        onClick={(e) => e.stopPropagation()}>
        <h3 id="tpl-apply-title" className="confirm-dialog-title">Apply Template</h3>

        {result ? (
          <>
            <ul className="tpl-apply-summary">
              <li>{plural(result.itemsAdded, 'document')} added</li>
              {result.partiesAdded?.length > 0 && <li>{plural(result.partiesAdded.length, 'party')} to be invited</li>}
              {result.formsAdded > 0 && <li>{plural(result.formsAdded, 'form')} added to the Forms tab</li>}
              {result.itemsSkipped > 0 && <li className="tpl-apply-muted">{plural(result.itemsSkipped, 'document')} already on this transaction — skipped</li>}
            </ul>
            {result.formsFailed?.length > 0 && (
              <p className="tpl-apply-error">Couldn't add: {result.formsFailed.join(', ')}. You can retry from the Forms tab.</p>
            )}
            <div className="confirm-dialog-actions">
              <button type="button" className="confirm-dialog-btn is-primary" onClick={handleDone}>Done</button>
            </div>
          </>
        ) : (
          <>
            <p className="confirm-dialog-body">
              Adds the template's documents and parties. Nothing already on this transaction is changed, and duplicates are skipped.
            </p>
            <TransactionTemplatePicker
              classPrefix="tpl-apply"
              side={side}
              agentState={agentState}
              primaryAgentId={transaction.primaryAgentId}
              templateId={templateId}
              onTemplateChange={setTemplateId}
              saleType={saleType}
              onSaleTypeChange={setSaleType}
              hideSaleType={!!transaction.saleType}
              allowNone={false}
            />
            {templateId && (
              <TemplatePreviewCard
                templateId={templateId}
                saleType={transaction.saleType || saleType}
                primaryAgentId={transaction.primaryAgentId}
              />
            )}
            {error && <p className="tpl-apply-error">{error}</p>}
            <div className="confirm-dialog-actions">
              <button type="button" className="confirm-dialog-btn" onClick={onClose} disabled={isApplying}>Cancel</button>
              <button type="button" className="confirm-dialog-btn is-primary" onClick={handleApply} disabled={!templateId || isApplying}>
                {isApplying ? 'Applying…' : 'Apply'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ApplyTemplateModal;
